import { createContext, forwardRef, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from '@sas/nova-core/axios';
import { MotionConfig } from 'framer-motion';
import { defaultThemes, resolveCustomApplicationTheme, setAlpha } from '../design-tokens';
import { renderReactGraphTokens } from '../design-tokens/graph-tokens/render/react';
import { renderReactDesignTokens } from '../design-tokens/render/react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useMounted } from '../hooks/useMounted';
import useOnFocusOut from '../hooks/useOnFocusOut';
import { getLanguage } from '../locale/useTextLocalizer';
import { useDeriveDesignTokens } from './useDeriveDesignTokens';

/**
 * A context that handles the current theme state.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const ThemeContext = /*#__PURE__*/createContext({
  currentTheme: defaultThemes.light,
  inverseTheme: defaultThemes.light
});

/**
 *  Default set of design tokens (light).
 */
export const defaultDesignTokens = renderReactDesignTokens(defaultThemes.light, {
  lang: getLanguage()
});

/**
 * Provides the design tokens state.
 */
const DesignTokenContext = /*#__PURE__*/createContext(defaultDesignTokens);

/**
 * Provides the graph tokens state.
 */
const GraphTokenContext = /*#__PURE__*/createContext(null);

/**
 * A context that maintains the current state of various application settings options.
 */
const GlobalSettingsThemeParametersContext = /*#__PURE__*/createContext({
  enableCustomFocus: false,
  customFocusColor: defaultDesignTokens.tokens['color-action-background-focus'],
  customFocusOpacity: '100',
  customFocusThickness: defaultDesignTokens.tokens['size-action-border-width-focus'],
  enableVisualEffects: true
});

/**
 * Provides the global setting state of the theme.
 */
const GlobalSettingsThemeParametersDispatchContext = /*#__PURE__*/createContext(undefined);

/**
 * @returns GlobalSettingsThemeParametersContext
 */
export function useGlobalSettingsThemeParameters() {
  return useContext(GlobalSettingsThemeParametersContext);
}

/**
 * @returns GlobalSettingsThemeParametersDispatchContext
 */
export function useGlobalSettingsThemeParametersDispatch() {
  const disptach = useContext(GlobalSettingsThemeParametersDispatchContext);
  if (disptach === undefined) {
    throw new Error('Global settings theme parameters dispatch must be used within GlobalSettingsThemeParametersDispatchContext Provider');
  }
  return disptach;
}

/**
 * A provider that maintains the current state of various application settings options.
 */
export const GlobalSettingsThemeParametersProvider = props => {
  const [enableCustomFocus, setEnableCustomFocus] = useState(false);
  const [customFocusColor, setCustomFocusColor] = useState();
  const [customFocusThickness, setCustomFocusThickness] = useState();
  const [customFocusOpacity, setCustomFocusOpacity] = useState();
  const [enableVisualEffects, setEnableVisualEffects] = useState(true);
  const globalSettingsThemeParams = useMemo(() => ({
    enableCustomFocus,
    customFocusColor,
    customFocusOpacity,
    customFocusThickness,
    enableVisualEffects
  }), [enableCustomFocus, customFocusColor, customFocusThickness, customFocusOpacity, enableVisualEffects]);
  const globalSettingsThemeParamsDispatch = useMemo(() => ({
    enableCustomFocus(value) {
      setEnableCustomFocus(value);
    },
    setCustomFocusColor(color) {
      setCustomFocusColor(color);
    },
    setCustomFocusThickness(width) {
      setCustomFocusThickness(width);
    },
    setCustomFocusOpacity(opacity) {
      setCustomFocusOpacity(opacity);
    },
    removeCustomFocusColor() {
      setCustomFocusColor(undefined);
    },
    removeCustomFocusThickness() {
      setCustomFocusThickness(undefined);
    },
    removeCustomFocusOpacity() {
      setCustomFocusOpacity(undefined);
    },
    enableVisualEffects(value) {
      setEnableVisualEffects(value);
    }
  }), []);
  return /*#__PURE__*/_jsx(GlobalSettingsThemeParametersContext.Provider, {
    value: globalSettingsThemeParams,
    children: /*#__PURE__*/_jsx(GlobalSettingsThemeParametersDispatchContext.Provider, {
      value: globalSettingsThemeParamsDispatch,
      children: props.children
    })
  });
};

/**
 *  A provider that maintains the current theme data.
 */
export const ThemeProvider = props => {
  const contextValue = useMemo(() => {
    const currentTheme = typeof props.currentTheme === 'string' ? defaultThemes[props.currentTheme] : props.currentTheme;
    const inverseTheme = typeof props.inverseTheme === 'string' ? defaultThemes[props.inverseTheme] : props.inverseTheme ?? currentTheme;
    return {
      currentTheme,
      inverseTheme
    };
  }, [props.currentTheme, props.inverseTheme]);
  return /*#__PURE__*/_jsx(ThemeContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(DesignTokenProvider, {
      contextValue: contextValue,
      children: props.children
    })
  });
};

/**
 * This container is  responsible for wrapping with the theme provider and global theme tokens container.
 */
export const ThemeContainer = /*#__PURE__*/forwardRef(({
  currentTheme,
  inverseTheme,
  children,
  ...restProps
}, ref) => {
  return /*#__PURE__*/_jsx(ThemeProvider, {
    currentTheme: currentTheme,
    inverseTheme: inverseTheme,
    children: /*#__PURE__*/_jsx(GlobalThemeTokensContainer, {
      ref: ref,
      ...restProps,
      children: children
    })
  });
});

/**
 * This container handles resolving custom and default application themes for Nova and
 * Nova-OpenUI hybrid applications. While themes are loading, the last successfully loaded
 * theme will be displayed.
 */
export const ViyaThemeContainer = props => {
  const {
    themeDefinition,
    viyaEndpoint = '',
    ...restProps
  } = props;
  const [theme, setTheme] = useState({
    currentTheme: defaultThemes.light,
    inverseTheme: undefined
  });
  // Feature flag for new token values
  const featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  {
    const appTheme = themeDefinition.type === 'application' ? themeDefinition.theme : undefined;
    const appThemeInverse = themeDefinition.type === 'application' ? themeDefinition.inverseTheme ?? undefined : undefined;
    useLayoutEffect(() => {
      if (!appTheme) {
        return;
      }
      setTheme({
        currentTheme: appTheme,
        inverseTheme: appThemeInverse
      });
    }, [appTheme, appThemeInverse]);
  }
  {
    const responseTheme = themeDefinition.type === 'response' ? themeDefinition.response : undefined;
    useLayoutEffect(() => {
      if (!responseTheme) {
        return;
      }
      setTheme(resolveCustomThemeToTheme(responseTheme, featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues));
    }, [featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues, responseTheme]);
  }
  {
    const customThemeId = themeDefinition.type === 'request' ? themeDefinition.themeId : undefined;
    useEffect(() => {
      let ignoreSetCustomTheme = false;
      if (!customThemeId) {
        return;
      }

      // Themes Service doc: http://swagger.na.sas.com/apis/themes/v5/apidoc.html
      axios.get(`${viyaEndpoint}/themes/themes/${customThemeId}`, {
        headers: {
          Accept: 'application/vnd.sas.theme.application.complete+json'
        }
      }).then(res => res.data).then(customThemeResponse => {
        // Ignore the resolved theme if viyaEndpoint or themeId change
        if (ignoreSetCustomTheme) return;

        // Ensure the theme service responds with the correct theme
        if (customThemeResponse?.id !== customThemeId) {
          // Falls back to the last specified theme
          return;
        }
        setTheme(resolveCustomThemeToTheme(customThemeResponse, featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues));
      });
      return () => {
        ignoreSetCustomTheme = true;
      };
    }, [customThemeId, featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues, viyaEndpoint]);
  }
  return /*#__PURE__*/_jsx(GlobalSettingsThemeParametersProvider, {
    children: /*#__PURE__*/_jsx(ThemeContainer, {
      ...restProps,
      ...theme,
      children: props.children
    })
  });
};

/**
 * Resolve a Viya custom theme response to an object containing the resolved Nova theme information
 *
 * @param themeResponse theme response "styleData" from Viya theme service
 */

export const resolveCustomThemeToTheme = (themeResponse, isNewTokenValueFlagEnabled) => ({
  currentTheme: resolveCustomApplicationTheme(themeResponse, undefined, isNewTokenValueFlagEnabled),
  // Temporarily set the inverseTheme to the currentTheme as we remove support for the Inspire theme.
  inverseTheme: resolveCustomApplicationTheme(themeResponse, undefined, isNewTokenValueFlagEnabled)
});

/**
 * Provides the inversion state of the theme.
 */
const InvertedThemeContext = /*#__PURE__*/createContext(false);

/**
 * Provides access to the inverse token system based on the specified theme and inversion settings.
 */
export const InverseTokenProvider = props => {
  const inverted = props.inverseTheme !== false;
  const contextValue = useContext(ThemeContext);
  return /*#__PURE__*/_jsx(InvertedThemeContext.Provider, {
    value: inverted,
    children: /*#__PURE__*/_jsx(DesignTokenProvider, {
      contextValue: contextValue,
      inverted: inverted,
      children: props.children
    })
  });
};

/**
 * Provides access to the design token system.
 */
export const DesignTokenProvider = props => {
  // Derive design tokens based on the provided contextValue and inversion flag
  const dt = useDeriveDesignTokens(props.contextValue, props.inverted);
  return /*#__PURE__*/_jsx(DesignTokenContext.Provider, {
    value: dt,
    children: props.children
  });
};

// This provides access to the graph token system. Since use cases are limited,
// this provider is not part of the AppRoot. Instead, consumers who need to theme UI
// pieces with the graph token set can add this provider as needed. The core 8 graph
// colors and 3 KPI colors are still available via useDesignTokens().
export const GraphTokenProvider = props => {
  const language = getLanguage();
  const theme = useThemeData();
  const dt = useDesignTokens();
  const gt = useMemo(() => {
    const tokens = renderReactGraphTokens(theme, {
      lang: language
    });
    // Set graphFocus styles based on derived design tokens
    const graphTokens = {
      ...tokens,
      graphFocus: {
        lineColor: dt.tokens['color-action-border-focus'],
        lineStyle: dt.tokens['style-action-border-focus'],
        lineThickness: dt.tokens['size-border-width-l']
      }
    };
    return graphTokens;
  }, [theme, language, dt]);
  return /*#__PURE__*/_jsx(GraphTokenContext.Provider, {
    value: gt,
    children: props.children
  });
};

/**
 * Provides the state of whether focus-related content should be displayed,
 * and with the state indicating whether the user is interacting via keyboard.
 */
const ShowFocusContext = /*#__PURE__*/createContext({
  showFocus: true,
  isKeyboardUser: true
});

/**
 * @returns ShowFocusContext
 */
export const useShowFocusContext = () => useContext(ShowFocusContext);

/**
 * Handles the management of global theme tokens, including design tokens, visual effects, and custom focus settings.
 */
const GlobalThemeTokensContainer = /*#__PURE__*/forwardRef(({
  className,
  style,
  children,
  ...restProps
}, ref) => {
  const mountedRef = useMounted();
  // Manage whether to show focus or not
  const [isKeyboardUser, setIsKeyboardUser] = useState(true);

  // Manage whether the container has a focused element
  const hasFocusRef = useRef(false);
  const dt = useDesignTokens();
  const {
    enableVisualEffects,
    enableCustomFocus
  } = useGlobalSettingsThemeParameters();
  const showFocus = enableCustomFocus || isKeyboardUser;
  const themeTokens = getThemeContainerTokens(dt, showFocus, enableVisualEffects);
  const {
    onBlur,
    onFocus
  } = useOnFocusOut(() => {
    if (!mountedRef.current) return;
    setIsKeyboardUser(true);
  });
  return /*#__PURE__*/_jsx(ShowFocusContext.Provider, {
    value: {
      showFocus,
      isKeyboardUser
    },
    children: /*#__PURE__*/_jsx(MotionConfig, {
      reducedMotion: enableVisualEffects ? 'user' : 'always',
      children: /*#__PURE__*/_jsx("div", {
        ...restProps,
        ref: ref,
        className: className,
        style: {
          ...themeTokens,
          ...style
        },
        "data-sas-theme": dt.themeVariant,
        onMouseDownCapture: () => {
          // MouseDown event is fired after onFocus event, so use setTimeout
          // to wait for hasFocusRef.current to be updated
          setTimeout(() => {
            if (!mountedRef.current) return;
            setIsKeyboardUser(!hasFocusRef.current);
          }, 0);
        },
        onKeyDownCapture: () => {
          setIsKeyboardUser(true);
        },
        onBlurCapture: () => {
          onBlur();
          hasFocusRef.current = false;
        },
        onFocusCapture: () => {
          onFocus();
          hasFocusRef.current = true;
        },
        children: children
      })
    })
  });
});

/**
 * Retrieves a basic set of "global"/inheritable tokens/CSS properties and values.
 */
export const getThemeContainerTokens = (dt, showFocus, enableVisualEffects) => {
  const showFocusTokens = dt.enableNovaTokens ? {
    onLightBg: {
      bd: dt.tokens['color-action-border-focus'],
      bdWidth: dt.tokens['size-action-border-width-focus'],
      bdStyle: dt.tokens['style-action-border-focus']
    },
    onDarkBg: {
      bd: dt.tokens['color-action-border-focus-inverse'],
      bdWidth: dt.tokens['size-action-border-width-focus'],
      bdStyle: dt.tokens['style-action-border-focus']
    },
    onRightClick: {
      bd: dt.tokens['color-action-background-focus'],
      bdWidth: dt.tokens['size-action-border-width-focus'],
      bdStyle: 'solid'
    }
  } : dt.stateFocus;
  const showFocusStyle = {
    '--focus-light-bg-color': showFocus ? showFocusTokens.onLightBg.bd : 'transparent',
    '--focus-dark-bg-color': showFocus ? showFocusTokens.onDarkBg.bd : 'transparent',
    '--focus-style': showFocusTokens.onLightBg.bdStyle,
    '--focus-width': showFocusTokens.onLightBg.bdWidth,
    '--focus-light-bg': 'var(--focus-light-bg-color) var(--focus-style) var(--focus-width)',
    '--focus-dark-bg': 'var(--focus-dark-bg-color) var(--focus-style) var(--focus-width)',
    '--nova-animation-duration-factor': enableVisualEffects ? '1' : '0'
  };
  let themeTokens;
  if (dt.enableNovaTokens) {
    themeTokens = {
      color: dt.tokens['color-text-neutral'],
      '--scrollbar-bg': dt.tokens['color-neutral'],
      '--scrollbar-hover-track-bg': 'transparent',
      '--selection-bg': dt.tokens['color-highlight-text'],
      '--selection-fg': dt.tokens['color-highlight-text-inverse'],
      ...showFocusStyle
    };
  } else {
    const selectionStyle = {
      '--selection-bg': dt.reportTokens.text.selection.bg,
      '--selection-fg': dt.reportTokens.text.selection.fg
    };
    switch (dt.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        themeTokens = {
          color: dt.c.white,
          '--scrollbar-bg': dt.c.white,
          '--scrollbar-hover-track-bg': dt.c.black,
          ...selectionStyle,
          ...showFocusStyle
        };
        break;
    }
    switch (dt.themeVariant) {
      case 'dark':
        themeTokens = {
          color: dt.c.white,
          '--scrollbar-bg': setAlpha(dt.c.n1, 0.75),
          '--scrollbar-hover-track-bg': dt.c.p_2,
          ...selectionStyle,
          ...showFocusStyle
        };
        break;
      case 'high-contrast':
        themeTokens = {
          color: dt.c.white,
          '--scrollbar-bg': dt.c.n2,
          '--scrollbar-hover-track-bg': dt.c.black,
          ...selectionStyle,
          ...showFocusStyle
        };
        break;
      default:
        themeTokens = {
          color: dt.c.n_3,
          '--scrollbar-bg': dt.c.n_3,
          '--scrollbar-hover-track-bg': dt.c.n4,
          ...selectionStyle,
          ...showFocusStyle
        };
    }
  }
  return {
    fontFamily: dt.enableNovaTokens ? dt.tokens['font-family-primary'] : dt.reportTokens.font.family.default,
    lineHeight: dt.enableNovaTokens ? dt.tokens['font-space-line-height'] : dt.reportTokens.font.lineHeight,
    ...themeTokens
  };
};

/**
 * @returns getThemeContainerTokens the object containing the global design tokens
 */
export function useGlobalDesignTokens() {
  const dt = useDesignTokens();
  const {
    showFocus
  } = useShowFocusContext();
  const {
    enableVisualEffects
  } = useGlobalSettingsThemeParameters();
  return getThemeContainerTokens(dt, showFocus, enableVisualEffects);
}

/**
 * @returns ThemeContext
 */
export function useThemeContext() {
  return useContext(ThemeContext);
}

/**
 * @returns the Theme parameters for the current theme
 */

export function useThemeData() {
  const inverted = useContext(InvertedThemeContext);
  const themeContext = useContext(ThemeContext);
  return inverted ? themeContext.inverseTheme : themeContext.currentTheme;
}

/**
 * @returns InvertedThemeContext
 */
export function useInvertedThemeData() {
  return useContext(InvertedThemeContext);
}

/**
 * @returns DesignTokenContext
 */
export function useDesignTokens() {
  return useContext(DesignTokenContext);
}

/**
 * @returns GraphTokenContext
 */
export function useGraphTokens() {
  const gt = useContext(GraphTokenContext);
  if (gt === null) {
    throw new Error('useGraphTokens must be used within GraphTokenProvider');
  }
  return gt;
}

/**
 * Transforms an object of keys and values into CSS Custom Properties with an optional prefix.
 *
 * @param values object of keys and values that are transformed to CSS Custom Properties
 * @param prefix an optional prefix for the CSS Custom Property
 * @returns an object of CSS Custom Properties cast as CSSProperties
 * @example
 *
 * ```tsx
 * const style = renderInlineCssVars({
 *   fg: 'red'
 * }, 'component');
 *
 * console.log(style); //{ '--component-fg': 'red' } : CSSProperties
 *
 * ```
 */
export function renderInlineCssVars(values, prefix = '') {
  if (!values) {
    return {};
  }
  if (prefix !== '') {
    prefix = `${prefix}-`;
  }
  const cssVars = {};
  for (const key of Object.keys(values)) {
    cssVars[`--${prefix}${key}`] = values[key];
  }
  return cssVars;
}

/**
 * Transforms an object of keys and values into CSS Custom Properties with a prefix.
 *
 * This is a more strongly typed version of {@link renderInlineCssVars}. The stronger typing
 * allows us to use this function when rendering tokens for the `theme` API.
 *
 * @param values object of keys and values that are transformed to CSS Custom Properties
 * @param prefix a required prefix for the CSS Custom Property. Generally the component name.
 * @returns an object of CSS Custom Properties with strict typing.
 * @example
 *
 * ```tsx
 * const style = renderDesignTokens({
 *   fg: 'red'
 * }, 'component');
 *
 * console.log(style); //{ '--component-fg': 'red' } : { '--component-fg': string }
 *
 * ```
 */
export const renderDesignTokens = (values, componentName) => {
  const p = `--${componentName}-`;
  const cssVars = {};
  Object.entries(values).forEach(([key, value]) => {
    const tk = `${p}${String(key)}`;
    cssVars[tk] = value;
  });
  return cssVars;
};
//# sourceMappingURL=theme.js.map